"use client";

import { AvailabilitySearchParameters } from "@/types/AvailabilitySearchParameters";
import constructAvailabilitySearchParametersQuery from "@/utils/constructAvailabilitySearchParametersQuery";
import { DateTime } from "luxon";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import SearchButton from "../buttons/SearchButton";
import AvailabilityCalendar from "../utils/AvailabilityCalendar";
import SelectOccupancy from "../utils/SelectOccupancy";
import SelectRate from "../utils/SelectRate";
import "./index.scss";

const CheckAvailabilityClient = ({
  availabilitySearchParameters,
  action,
  displayNumberOfGuests = false,
  minimumChildGroupAges = [],
  allowsPets = false,
  alignDatepicker = "left",
  autoSubmit = false,
  acceptsPromotionCodes = true,
  customerGroups = [],
  maximumNumberOfAdults,
  maximumNumberOfChildren,
  maximumNumberOfPets,
}: {
  availabilitySearchParameters: AvailabilitySearchParameters;
  action: string;
  displayNumberOfGuests: boolean;
  minimumChildGroupAges: number[];
  allowsPets: boolean;
  alignDatepicker: "left" | "center" | "right";
  autoSubmit?: boolean;
  acceptsPromotionCodes?: boolean;
  customerGroups?: string[];
  maximumNumberOfAdults: number;
  maximumNumberOfChildren: number;
  maximumNumberOfPets: number;
}) => {
  const t = useTranslations("CheckAvailability");

  const router = useRouter();

  const [
    internalAvailabilitySearchParameters,
    setInternalAvailabilitySearchParameters,
  ] = useState(availabilitySearchParameters);

  useEffect(() => {
    setInternalAvailabilitySearchParameters(availabilitySearchParameters);
  }, [availabilitySearchParameters]);

  const startDateLuxon = DateTime.fromISO(
    internalAvailabilitySearchParameters.startDate
  );
  const endDateLuxon = DateTime.fromISO(
    internalAvailabilitySearchParameters.endDate
  );
  const [openedModal, setOpenedModal] = useState<
    "CHECK_IN" | "CHECK_OUT" | "GUESTS" | "RATE" | null
  >(null);

  useEffect(() => {
    if (openedModal) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setOpenedModal(null);
        }
      };

      const handleClick = (e: MouseEvent) => {
        const isClickInsideComponent = !!(e.target as Element).closest(
          "form.component.check-availability div.check-availability-inner-wrapper"
        );

        const isClickInsideSearchButton = !!(e.target as Element).closest(
          "form.component.check-availability button.search-button"
        );

        if (isClickInsideComponent && !isClickInsideSearchButton) {
          // do nothing
        } else {
          setOpenedModal(null);
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("click", handleClick);

      return () => {
        window.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("click", handleClick);
      };
    }
  }, [!!openedModal]);

  const { numberOfAdults, numberOfChildren, numberOfPets } =
    internalAvailabilitySearchParameters;
  const totalNumberOfGuests = numberOfAdults + numberOfChildren;

  const submitAvailabilitySearchParameters = (
    availabilitySearchParameters: AvailabilitySearchParameters
  ) => {
    const availabilitySearchParametersQuery =
      constructAvailabilitySearchParametersQuery(availabilitySearchParameters);

    const url = `${action}?${availabilitySearchParametersQuery}`;
    router.push(url, { scroll: false });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    submitAvailabilitySearchParameters(internalAvailabilitySearchParameters);
  };

  return (
    <form
      className={`component check-availability component-align-${alignDatepicker} modal-${
        openedModal ? "opened" : "closed"
      }`}
      action={action}
      method="GET"
      role="search"
      onSubmit={handleSubmit}
    >
      <div className="check-availability-inner-wrapper">
        <div className="check-availability-inner-wrapper-2">
          <div className="check-availability-layout">
            <div className="section search-dates">
              <div
                className={`field check-in ${
                  openedModal === "CHECK_IN"
                    ? "input-active"
                    : "input-not-active"
                }`}
                onClick={() => {
                  if (openedModal === "CHECK_IN") {
                    setOpenedModal(null);
                  } else {
                    setOpenedModal("CHECK_IN");
                  }
                }}
              >
                <div className="field-label">{t("check-in")}</div>
                <div className="field-value">
                  {startDateLuxon.isValid ? (
                    <span
                      className="date"
                      data-value={
                        internalAvailabilitySearchParameters.startDate
                      }
                    >
                      {startDateLuxon.toLocaleString({
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  ) : (
                    <span className="add-dates">{t("add-dates")}</span>
                  )}
                </div>
              </div>
              <div
                className={`field check-out ${
                  openedModal === "CHECK_OUT"
                    ? "input-active"
                    : "input-not-active"
                } ${
                  openedModal === null ||
                  openedModal === "CHECK_IN" ||
                  openedModal === "RATE"
                    ? "show-divider"
                    : ""
                }`}
                onClick={() => {
                  if (openedModal === "CHECK_OUT") {
                    setOpenedModal(null);
                  } else {
                    setOpenedModal("CHECK_OUT");
                  }
                }}
              >
                <div className="field-label">{t("check-out")}</div>
                <div className="field-value">
                  {endDateLuxon.isValid ? (
                    <span
                      className="date"
                      data-value={internalAvailabilitySearchParameters.endDate}
                    >
                      {endDateLuxon.toLocaleString({
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  ) : (
                    <span className="add-dates">{t("add-dates")}</span>
                  )}
                </div>
              </div>
              {(openedModal === "CHECK_IN" || openedModal === "CHECK_OUT") && (
                <div
                  className={`availability-calendar-wrapper ${alignDatepicker}`}
                >
                  <AvailabilityCalendar
                    availabilitySearchParameters={
                      internalAvailabilitySearchParameters
                    }
                    onChange={(startDate, endDate) => {
                      const newInternalAvailabilitySearchParameters = {
                        ...internalAvailabilitySearchParameters,
                        startDate,
                        endDate,
                      };
                      setInternalAvailabilitySearchParameters(
                        newInternalAvailabilitySearchParameters
                      );
                      setOpenedModal(null);
                      if (autoSubmit) {
                        submitAvailabilitySearchParameters(
                          newInternalAvailabilitySearchParameters
                        );
                      }
                    }}
                  />
                </div>
              )}
            </div>

            <div className="section guests">
              <div
                className={`field guests ${
                  openedModal === "GUESTS" ? "input-active" : "input-not-active"
                }`}
                onClick={() => {
                  if (openedModal === "GUESTS") {
                    setOpenedModal(null);
                  } else {
                    setOpenedModal("GUESTS");
                  }
                }}
              >
                <div className="field-label">{t("guests")}</div>
                <div className="field-value">
                  {t("number-of-guests-and-pets", {
                    guests: totalNumberOfGuests,
                    pets: numberOfPets,
                  })}
                </div>
              </div>

              {openedModal === "GUESTS" && (
                <div
                  className={`select-occupancy-wrapper align-${alignDatepicker}`}
                >
                  <SelectOccupancy
                    numberOfAdults={
                      internalAvailabilitySearchParameters.numberOfAdults
                    }
                    numberOfChildren={
                      internalAvailabilitySearchParameters.numberOfChildren
                    }
                    childAges={internalAvailabilitySearchParameters.childAges}
                    numberOfPets={
                      internalAvailabilitySearchParameters.numberOfPets
                    }
                    onChangeOccupancy={(
                      numberOfAdults,
                      numberOfChildren,
                      childAges,
                      numberOfPets
                    ) => {
                      const newInternalAvailabilitySearchParameters = {
                        ...internalAvailabilitySearchParameters,
                        numberOfAdults,
                        numberOfChildren,
                        childAges,
                        numberOfPets,
                      };
                      setInternalAvailabilitySearchParameters(
                        newInternalAvailabilitySearchParameters
                      );
                      if (autoSubmit) {
                        submitAvailabilitySearchParameters(
                          newInternalAvailabilitySearchParameters
                        );
                      }
                    }}
                    displayNumberOfGuests={displayNumberOfGuests}
                    minimumChildGroupAges={minimumChildGroupAges}
                    allowsPets={allowsPets}
                    maximumNumberOfAdults={maximumNumberOfAdults}
                    maximumNumberOfChildren={maximumNumberOfChildren}
                    maximumNumberOfPets={maximumNumberOfPets}
                  />
                </div>
              )}
            </div>

            {(acceptsPromotionCodes || customerGroups.length > 0) && (
              <div className="section rates">
                <div
                  className="field rates"
                  onClick={() => {
                    if (openedModal === "RATE") {
                      setOpenedModal(null);
                    } else {
                      setOpenedModal("RATE");
                    }
                  }}
                >
                  <div className="field-label">{t("special-rates")}</div>
                  <div className="field-value">
                    {internalAvailabilitySearchParameters.couponCode &&
                    internalAvailabilitySearchParameters.couponCode.length > 0
                      ? t("promotion-code-value", {
                          promotionCode:
                            internalAvailabilitySearchParameters.couponCode,
                        })
                      : internalAvailabilitySearchParameters.customerGroup &&
                          internalAvailabilitySearchParameters.customerGroup
                            .length > 0
                        ? internalAvailabilitySearchParameters.customerGroup
                        : t("best-available-rate")}
                  </div>
                </div>
                {openedModal === "RATE" && (
                  <div className={`select-rate-wrapper ${alignDatepicker}`}>
                    <SelectRate
                      customerGroup={
                        internalAvailabilitySearchParameters.customerGroup
                      }
                      couponCode={
                        internalAvailabilitySearchParameters.couponCode
                      }
                      onChangeRate={(customerGroup, couponCode) => {
                        const newInternalAvailabilitySearchParameters = {
                          ...internalAvailabilitySearchParameters,
                          customerGroup,
                          couponCode,
                        };
                        setInternalAvailabilitySearchParameters(
                          newInternalAvailabilitySearchParameters
                        );
                        if (autoSubmit) {
                          submitAvailabilitySearchParameters(
                            newInternalAvailabilitySearchParameters
                          );
                        }
                      }}
                      customerGroups={customerGroups}
                      displayPromotionCode={true}
                    />
                  </div>
                )}
              </div>
            )}

            {autoSubmit === false && (
              <div className="section check-availability-actions">
                <SearchButton type="submit" />
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
};
export default CheckAvailabilityClient;
